import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import type {
  ParallelAgentExecution,
  ParallelAgentTerminalStatus,
} from './parallel-agent-execution';
import type { SubagentActivityItem } from './subagent-activity';

export const PARALLEL_AGENT_BATCH_MIN_SIZE = 1;

export type ParallelAgentStatus =
  | 'running'
  | 'waiting'
  | 'completed'
  | 'error'
  | 'aborted';

export type ParallelAgentBatchSummary = {
  aliveCount: number;
  completedCount: number;
  errorCount: number;
  abortedCount: number;
  isFinished: boolean;
  status: ParallelAgentStatus;
};

export type LatestToolActivity = {
  label: string;
  target: string;
  isRunning: boolean;
};

export function parallelAgentStatus(
  execution: ParallelAgentExecution,
): ParallelAgentStatus {
  if (execution.terminalStatus) {
    return execution.terminalStatus;
  }

  return execution.waitingInput ? 'waiting' : 'running';
}

export function isParallelAgentAlive(status: ParallelAgentStatus): boolean {
  return status === 'running' || status === 'waiting';
}

const LIVE_TASK_TOOL_NAMES = new Set([
  'task',
  'recon',
  'operator',
  'intelligence',
  'validator',
  'code-analyst',
  'skill-builder',
  'application-data-analyst',
  'object-creator',
  'general',
]);

const LIVE_TASK_STATUSES = new Set(['started', 'in-progress']);

const TERMINAL_TASK_STATUSES: Record<string, ParallelAgentTerminalStatus> = {
  completed: 'completed',
  error: 'error',
  aborted: 'aborted',
};

export function liveTaskExecutionsFromMessages({
  messages,
  chatId,
}: {
  messages: readonly TerraUIMessage[];
  chatId: string;
}): ParallelAgentExecution[] {
  return taskExecutionsFromMessages({ messages, chatId }).filter(
    (execution) => !execution.terminalStatus,
  );
}

export function taskExecutionsFromMessages({
  messages,
  chatId,
}: {
  messages: readonly TerraUIMessage[];
  chatId: string;
}): ParallelAgentExecution[] {
  const byToolCallId = new Map<string, ParallelAgentExecution>();

  for (const message of messages) {
    for (const part of message.parts ?? []) {
      if (part.type !== TerraUIMessageDataPartName) {
        continue;
      }

      const data = part.data;

      if (!data || typeof data !== 'object') {
        continue;
      }

      const record = data as Record<string, unknown>;
      const type = record.type;
      const isToolPart =
        type === DataMessageTypeEnum.ToolCallStart ||
        type === DataMessageTypeEnum.ToolCallUpdate ||
        type === DataMessageTypeEnum.ToolCall ||
        type === DataMessageTypeEnum.ToolCallComplete ||
        type === DataMessageTypeEnum.ToolCallError;

      if (!isToolPart) {
        continue;
      }

      const toolName =
        typeof record.toolName === 'string'
          ? record.toolName.replace(/_/g, '-').toLowerCase()
          : '';
      const toolCallId = record.toolCallId;
      const metadata = record.metadata;
      const parentToolCallId =
        metadata &&
        typeof metadata === 'object' &&
        typeof (metadata as { parentToolCallId?: unknown }).parentToolCallId ===
          'string'
          ? (metadata as { parentToolCallId: string }).parentToolCallId
          : undefined;

      if (
        typeof toolCallId !== 'string' ||
        !LIVE_TASK_TOOL_NAMES.has(toolName)
      ) {
        continue;
      }

      if (parentToolCallId) {
        byToolCallId.delete(toolCallId);
        continue;
      }

      const status = typeof record.status === 'string' ? record.status : '';
      const terminalStatus = TERMINAL_TASK_STATUSES[status];

      if (terminalStatus) {
        const previous = byToolCallId.get(toolCallId);

        if (previous) {
          byToolCallId.set(toolCallId, { ...previous, terminalStatus });
        }

        continue;
      }

      if (!LIVE_TASK_STATUSES.has(status)) {
        continue;
      }

      const input = record.input;
      const subagentType =
        input &&
        typeof input === 'object' &&
        typeof (input as { subagent_type?: unknown }).subagent_type === 'string'
          ? (input as { subagent_type: string }).subagent_type
          : toolName === 'task'
            ? 'subagent'
            : toolName;

      byToolCallId.set(toolCallId, {
        copilotThreadId: chatId,
        agentThreadId: toolCallId,
        toolCallId,
        agentType: subagentType,
        kind: 'subagent',
      });
    }
  }

  return [...byToolCallId.values()];
}

export function mergeTrackedAndLiveExecutions({
  tracked,
  live,
}: {
  tracked: readonly ParallelAgentExecution[];
  live: readonly ParallelAgentExecution[];
}): ParallelAgentExecution[] {
  const byToolCallId = new Map<string, ParallelAgentExecution>(
    live.map((execution) => [execution.toolCallId, execution]),
  );

  for (const execution of tracked) {
    byToolCallId.set(execution.toolCallId, execution);
  }

  return [...byToolCallId.values()];
}

export function trackedAgentExecutionsForChat<
  TExecution extends ParallelAgentExecution,
>({
  executions,
  chatId,
}: {
  executions: Iterable<TExecution>;
  chatId: string;
}): TExecution[] {
  return [...executions].filter(
    (execution) =>
      execution.copilotThreadId === chatId &&
      (execution.kind === 'subagent' || execution.kind === 'neo'),
  );
}

export function nextParallelAgentBatchMemberIds({
  previousMemberIds,
  executions,
}: {
  previousMemberIds: readonly string[];
  executions: readonly ParallelAgentExecution[];
}): string[] {
  const presentIds = new Set(
    executions.map((execution) => execution.toolCallId),
  );
  const aliveIds = executions
    .filter((execution) => isParallelAgentAlive(parallelAgentStatus(execution)))
    .map((execution) => execution.toolCallId);
  const presentPreviousIds = previousMemberIds.filter((id) =>
    presentIds.has(id),
  );
  const previousBatchIsAlive = presentPreviousIds.some((id) =>
    aliveIds.includes(id),
  );

  if (previousBatchIsAlive) {
    return [
      ...presentPreviousIds,
      ...aliveIds.filter((id) => !presentPreviousIds.includes(id)),
    ];
  }

  if (aliveIds.length >= PARALLEL_AGENT_BATCH_MIN_SIZE) {
    return aliveIds;
  }

  return aliveIds.length > 0 ? [] : presentPreviousIds;
}

export function summarizeParallelAgentBatch(
  members: readonly ParallelAgentExecution[],
): ParallelAgentBatchSummary {
  const statuses = members.map(parallelAgentStatus);
  const countOf = (status: ParallelAgentStatus) =>
    statuses.filter((candidate) => candidate === status).length;
  const aliveCount = statuses.filter(isParallelAgentAlive).length;
  const errorCount = countOf('error');
  const abortedCount = countOf('aborted');
  const isFinished = members.length > 0 && aliveCount === 0;

  return {
    aliveCount,
    completedCount: countOf('completed'),
    errorCount,
    abortedCount,
    isFinished,
    status: !isFinished
      ? 'running'
      : errorCount > 0
        ? 'error'
        : abortedCount === members.length
          ? 'aborted'
          : 'completed',
  };
}

export function latestToolActivity(
  items: readonly SubagentActivityItem[],
): LatestToolActivity | undefined {
  const latest = items.findLast((item) => item.kind === 'tool');

  if (!latest || latest.kind !== 'tool') {
    return undefined;
  }

  const isRunning =
    latest.status === 'started' || latest.status === 'in-progress';

  return {
    label: isRunning ? latest.labels.labelPresent : latest.labels.labelPast,
    target: latest.target,
    isRunning,
  };
}

export function sameMemberIds(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((id, index) => id === right[index])
  );
}
